import { supabase } from "../lib/supabaseClient";
import { ChatSession } from "../types";
import { isAuthMockMode } from "./auth";

export let mockSessions: ChatSession[] = [];

export const getSessions = async (): Promise<ChatSession[]> => {
  if (isAuthMockMode) {
    return [...mockSessions];
  }

  const { data: sessions } = await supabase!
    .from("chat_sessions")
    .select("*")
    .order("created_at", { ascending: false });

  return (sessions || []).map((s) => ({
    id: s.id,
    title: s.title || "New Chat",
    createdAt: new Date(s.created_at),
    messages: [],
  }));
};

export const getSessionDetails = async (
  sessionId: string,
): Promise<ChatSession | undefined> => {
  if (isAuthMockMode) {
    return mockSessions.find((s) => s.id === sessionId);
  }

  const { data: session } = await supabase!
    .from("chat_sessions")
    .select("*")
    .eq("id", sessionId)
    .single();

  if (!session) return undefined;

  const { data: messages } = await supabase!
    .from("messages")
    .select("*")
    .eq("session_id", sessionId)
    .order("created_at", { ascending: true });

  return {
    id: session.id,
    title: session.title,
    createdAt: new Date(session.created_at),
    messages: (messages || []).map((dbMsg: any) => {
      const ta = dbMsg.tool_approval_data;
      let finalTA = undefined;
      let finalLedger = undefined;
      let finalIntervention = undefined;
      if (ta) {
        if (ta._isLedger) {
          finalLedger = ta.ledger;
        } else if (ta._isIntervention) {
          finalIntervention = { ...ta.intervention, status: 'pending' };
        } else if (ta.id) {
          finalTA = {
            id: ta.id,
            toolName: ta.toolName,
            args: ta.args,
            status: ta.status,
            result: ta.result,
            description: ta.description,
          };
        }
      }
      return {
        id: dbMsg.id,
        type: dbMsg.type,
        content: dbMsg.content,
        timestamp: new Date(dbMsg.created_at),
        attachment: dbMsg.attachment_info,
        toolApproval: finalTA,
        ledger: finalLedger,
        intervention: finalIntervention,
        summary: dbMsg.summary,
        creditsInfo: dbMsg.credits_used ? { used: dbMsg.credits_used, remaining: 0 } : undefined
      };
    }),
  };
};

export const updateApprovalStatus = async (approvalId: string, newStatus: 'approved' | 'executed' | 'rejected', newArgs?: any): Promise<void> => {
  if (isAuthMockMode || !supabase) return;
  try {
    const { data: rows, error } = await supabase
      .from('messages')
      .select('id, tool_approval_data')
      .filter('tool_approval_data->>id', 'eq', approvalId)
      .limit(1);

    if (error || !rows || rows.length === 0) {
      console.warn('[Approval] Could not find message row for approval', approvalId, error);
      return;
    }

    const row = rows[0];
    const existing = row.tool_approval_data || {};
    const updated = {
      ...existing,
      status: newStatus,
      args: newArgs || existing.args,
    };

    const { error: updateError } = await supabase
      .from('messages')
      .update({ tool_approval_data: updated })
      .eq('id', row.id);

    if (updateError) {
      console.warn('[Approval] Failed to persist approval status:', updateError);
    } else {
      console.log(`[Approval] Persisted ${approvalId} → ${newStatus}`);
    }
  } catch (e) {
    console.warn('[Approval] updateApprovalStatus caught:', e);
  }
};

export const deleteSession = async (sessionId: string): Promise<void> => {
  if (isAuthMockMode) {
    mockSessions = mockSessions.filter((s) => s.id !== sessionId);
    return;
  }

  const {
    data: { user },
  } = await supabase!.auth.getUser();
  if (!user) throw new Error("Authentication required for deletion");

  console.log(`Attempting to delete session ${sessionId} for user ${user.id}`);

  const { error: msgError } = await supabase!
    .from("messages")
    .delete()
    .eq("session_id", sessionId)
    .eq("user_id", user.id);

  if (msgError) {
    throw new Error(`Failed to delete messages: ${msgError.message}`);
  }

  const { error: sessionError } = await supabase!
    .from("chat_sessions")
    .delete()
    .eq("id", sessionId)
    .eq("user_id", user.id);

  if (sessionError) {
    throw new Error(`Failed to delete session: ${sessionError.message}`);
  }
};

export const clearHistory = async (): Promise<void> => {
  if (isAuthMockMode) {
    mockSessions = [];
    return;
  }

  const {
    data: { user },
  } = await supabase!.auth.getUser();
  if (!user) throw new Error("Authentication required for clearing history");

  const { error: msgError } = await supabase!
    .from("messages")
    .delete()
    .eq("user_id", user.id);

  if (msgError) {
    throw new Error(`Failed to clear messages: ${msgError.message}`);
  }

  const { error: sessionError } = await supabase!
    .from("chat_sessions")
    .delete()
    .eq("user_id", user.id);

  if (sessionError) {
    throw new Error(`Failed to clear history: ${sessionError.message}`);
  }
};

export const renameSession = async (sessionId: string, title: string): Promise<void> => {
  if (isAuthMockMode) {
    mockSessions = mockSessions.map((s) =>
      s.id === sessionId ? { ...s, title } : s,
    );
    return;
  }

  const {
    data: { user },
  } = await supabase!.auth.getUser();
  if (!user) throw new Error("Authentication required for rename");

  const { error } = await supabase!
    .from("chat_sessions")
    .update({ title })
    .eq("id", sessionId)
    .eq("user_id", user.id);

  if (error) {
    throw new Error(`Failed to rename session: ${error.message}`);
  }
};
